import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider,
  List, ListItemButton, Chip, CircularProgress,
} from '@mui/material'
import CfpSelect from '../../comps/CfpSelect'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { addToast } from '../../services/Toasts'
import { FS_MD, FS_SM, FS_XS, ICON_MD, ICON_SM, COLOR_PRIMARY, COLOR_PRIMARY_HOVER1, COLOR_PRIMARY_HOVER2 } from '../../common/theme'
import ResizableDivider from '../../comps/ResizableDivider'
import { saveStyleGroup, renameStyleGroup, deleteStyleGroup, deleteStyleTag } from '../../services/TemplatesService'
import InputModal from '../modals/InputModal'
import ConfirmModal from '../modals/ConfirmModal'
import type { TaStyleTabManageProps, StyleTagType } from '../../types'

const editableSx = {
  flex: 1, fontSize: FS_MD, lineHeight: 1.5, px: 0.5, py: 0.25,
  border: '1px solid transparent', borderRadius: 0.5,
  '&:focus': { outline: 'none', borderColor: 'primary.main', borderStyle: 'solid' },
  '&:empty::before': { content: 'attr(data-placeholder)', color: 'text.disabled', pointerEvents: 'none' },
}

interface StyleTag {
  tagsha?: string
  type: StyleTagType
  valuehtml?: string
  tagnamehtml?: string
}

interface StyleGroup {
  name: string
  styletags?: StyleTag[]
  updated?: string
}

export default function ManageStylesTab({ styleTemplates, onRefresh }: TaStyleTabManageProps) {
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [sortProp, setSortProp] = useState('name')
  const [addOpen, setAddOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState(false)
  const [deleteItemTarget, setDeleteItemTarget] = useState<StyleTag | null>(null)
  const [saving, setSaving] = useState(false)

  // Local working copy of the selected template - edits go here, then `Save`
  // posts to /api2/smsavestylegroup. Re-syncs when the parent's list changes
  // (eg. after a refresh).
  const [draft, setDraft] = useState<StyleGroup | null>(null)
  const [lhsWidth, setLhsWidth] = useState(220)

  // Auto-select the first template when the list arrives.
  useEffect(() => {
    if (selectedName == null && styleTemplates.length > 0) {
      setSelectedName(styleTemplates[0].name)
    }
  }, [styleTemplates, selectedName])

  // Pull the canonical template from the prop list, then mirror into draft
  // whenever the selected name (or upstream data) changes.
  useEffect(() => {
    const found = styleTemplates.find(s => s.name === selectedName) ?? null
    setDraft(found ? structuredClone(found) as StyleGroup : null)
  }, [selectedName, styleTemplates])

  const sorted = useMemo(() => (
    [...styleTemplates].sort((a, b) => String(a[sortProp] ?? '').localeCompare(String(b[sortProp] ?? '')))
  ), [styleTemplates, sortProp])

  const tagsByType = (type: StyleTagType): StyleTag[] =>
    (draft?.styletags ?? []).filter(t => t.type === type)

  const handleAddTemplate = async (name: string) => {
    if (!name.trim()) return
    try {
      // Backend rejects empty styletags arrays, so seed with one placeholder
      // entry (matches the angularjs `addstyletemplatefn` shape).
      await saveStyleGroup({
        name: name.trim(),
        styletags: [{ name: name.trim(), type: 'aii', value: '' }],
      })
      addToast('success', 'Style template added.')
      setSelectedName(name.trim())
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to add style template.')
    }
  }

  const handleRenameTemplate = async (newname: string) => {
    if (!draft || !newname.trim()) return
    try {
      await renameStyleGroup(draft.name, newname.trim(), 'aii')
      addToast('success', 'Style template renamed.')
      setSelectedName(newname.trim())
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to rename style template.')
    }
  }

  const handleDeleteTemplate = async () => {
    if (!draft) return
    try {
      await deleteStyleGroup(draft.name, 'aii')
      addToast('success', 'Style template deleted.')
      setSelectedName(null)
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to delete style template.')
    }
  }

  const handleSaveTemplate = async () => {
    if (!draft) return
    setSaving(true)
    try {
      // Every styletag must carry the group's name on save (matches the
      // angularjs payload - the backend uses tag.name to locate the group).
      const payload = {
        ...draft,
        styletags: (draft.styletags ?? []).map(t => ({ ...t, name: draft.name })),
      }
      await saveStyleGroup(payload)
      addToast('success', 'Style template saved.')
      onRefresh?.()
    } catch {
      addToast('error', 'Failed to save style template.')
    } finally {
      setSaving(false)
    }
  }

  const handleAddTag = (type: StyleTagType) => {
    setDraft(prev => prev
      ? { ...prev, styletags: [...(prev.styletags ?? []), { name: prev.name, type, valuehtml: '', tagnamehtml: '' }] }
      : prev
    )
  }

  const handleEditTagValue = (tag: StyleTag, html: string) => {
    setDraft(prev => prev
      ? { ...prev, styletags: (prev.styletags ?? []).map(t => t === tag ? { ...t, valuehtml: html } : t) }
      : prev
    )
  }

  const handleDeleteItem = async () => {
    if (!deleteItemTarget) return
    try {
      // If it's been persisted (has tagsha) hit the API; otherwise it's a
      // local-only addition we just need to drop from draft.
      if (deleteItemTarget.tagsha) {
        await deleteStyleTag(deleteItemTarget.tagsha)
      }
      setDraft(prev => prev
        ? { ...prev, styletags: (prev.styletags ?? []).filter(t => t !== deleteItemTarget) }
        : prev
      )
      addToast('success', 'Item deleted.')
      // Refresh upstream so a later save picks up the right state.
      if (deleteItemTarget.tagsha) onRefresh?.()
    } catch {
      addToast('error', 'Failed to delete item.')
    } finally {
      setDeleteItemTarget(null)
    }
  }

  const renderSection = (title: string, type: StyleTagType, placeholder: string) => {
    const tags = tagsByType(type)
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>{title}</Typography>
          <Tooltip title={`Add ${title}`}>
            <IconButton size="small" onClick={() => handleAddTag(type)}>
              <AddIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        {tags.length === 0 && (
          <Typography variant="caption" sx={{ opacity: 0.5, px: 0.5, display: 'block', py: 0.25 }}>None</Typography>
        )}
        {tags.map((tag, i) => (
          <Box key={tag.tagsha ?? `new-${i}`} sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5, gap: 0.5 }}>
            <Box
              component="span"
              contentEditable
              suppressContentEditableWarning
              data-placeholder={placeholder}
              onBlur={(e: React.FocusEvent<HTMLSpanElement>) => handleEditTagValue(tag, e.currentTarget.innerHTML)}
              dangerouslySetInnerHTML={{ __html: tag.valuehtml ?? '' }}
              sx={editableSx}
            />
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setDeleteItemTarget(tag)}
                sx={{ opacity: 0.45, '&:hover': { opacity: 1 }, flexShrink: 0 }}
              >
                <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>

      {/* Modals */}
      <InputModal
        open={addOpen}
        title="Add Style Template"
        okLabel="Add"
        placeholder="Template name"
        singleLine
        testIdPrefix="sm_addstyle"
        onConfirm={handleAddTemplate}
        onClose={() => setAddOpen(false)}
      />
      <InputModal
        open={renameOpen}
        title="Rename Style Template"
        okLabel="Rename"
        placeholder="New name"
        singleLine
        initialValue={draft?.name ?? ''}
        testIdPrefix="sm_renamestyle"
        onConfirm={handleRenameTemplate}
        onClose={() => setRenameOpen(false)}
      />
      <ConfirmModal
        open={deleteTemplateOpen}
        title="Delete Style Template"
        body={`Are you sure you want to delete "${draft?.name}"?`}
        okLabel="Delete"
        testIdPrefix="sm_deletestyle"
        onConfirm={handleDeleteTemplate}
        onClose={() => setDeleteTemplateOpen(false)}
      />
      <ConfirmModal
        open={!!deleteItemTarget}
        title="Delete Item"
        body="Are you sure you want to delete this item?"
        okLabel="Delete"
        testIdPrefix="sm_deletetag"
        onConfirm={handleDeleteItem}
        onClose={() => setDeleteItemTarget(null)}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Style Templates</Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={onRefresh}><RefreshIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
        <Tooltip title="Add Style Template">
          <IconButton size="small" onClick={() => setAddOpen(true)}><AddIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
      </Box>
      <Divider />

      {/* Split panel */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* LHS list */}
        <Box sx={{ width: lhsWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5 }}>
            <CfpSelect
              label="Order by"
              value={sortProp}
              onChange={setSortProp}
              options={[
                { value: 'name', label: 'Name' },
                { value: 'updated', label: 'Updated' },
              ]}
            />
          </Box>
          <List disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
            {sorted.length === 0 && (
              <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_XS }}>
                  No templates yet
                </Typography>
              </Box>
            )}
            {sorted.map((s, i) => {
              const isSelected = s.name === selectedName
              return (
                <ListItemButton key={s.name ?? i} selected={isSelected} onClick={() => setSelectedName(s.name)}
                  sx={{
                    py: 1.5, px: 1.5,
                    borderLeft: 3, borderLeftColor: 'transparent',
                    transition: 'background-color 0.12s, border-color 0.12s',
                    '&:not(.Mui-selected)': {
                      bgcolor: i % 2 === 1 ? 'rgba(0,0,0,0.08)' : 'transparent',
                      '&:hover': { bgcolor: COLOR_PRIMARY_HOVER1 },
                    },
                    '&.Mui-selected': {
                      bgcolor: COLOR_PRIMARY_HOVER2,
                      borderLeftColor: COLOR_PRIMARY,
                      '&:hover': { bgcolor: COLOR_PRIMARY_HOVER2 },
                    },
                  }}>
                  <Typography variant="caption" sx={{
                    fontSize: FS_SM,
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                  }}>{s.name}</Typography>
                </ListItemButton>
              )
            })}
          </List>
        </Box>

        {/* Panel divider - drag to resize the LHS list */}
        <ResizableDivider onDelta={(dx) => setLhsWidth(w => Math.max(140, Math.min(500, w + dx)))} />

        {/* RHS */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            {draft ? (
              <Chip size="small" icon={<LayersOutlinedIcon />} label={draft.name} sx={{ mr: 'auto', fontSize: FS_XS }} />
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            <Tooltip title="Save Template">
              <span>
                <IconButton size="small" disabled={!draft || saving} onClick={handleSaveTemplate}>
                  {saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: ICON_MD }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rename Template">
              <span>
                <IconButton size="small" disabled={!draft} onClick={() => setRenameOpen(true)}>
                  <EditIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Template">
              <span>
                <IconButton size="small" disabled={!draft} onClick={() => setDeleteTemplateOpen(true)}>
                  <DeleteOutlineIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {draft ? (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
              {renderSection('Business Knowledge', 'tag', 'Your business knowledge')}
              {renderSection('Tone of Voice', 'tov', 'Your tone of voice')}
              {renderSection('Extra Instructions for AI', 'aii', 'Instruction for the AI when generating text')}
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.4 }}>Select a template</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

